import { getOperationName } from '@apollo/client/utilities';
import { IonList, IonText } from '@ionic/react';
import { Formik } from 'formik';
import { i18n } from 'i18next';
import { ReactElement, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { usePermissions } from '../../api/authorization.provider';
import {
  GetMachineDocument,
  Machine,
  MachineMaintenanceSchedule,
  MachineMaintenanceScheduleDayOfWeek,
  useGetMachineQuery,
  useUpdateMachineMaintenanceMutation,
} from '../../api/graphql/generated';
import { IntercomArticle } from '../../config/intercom';
import { RightAlignedButtons } from '../button/aligned-buttons';
import { LoadingButton } from '../button/loading-button';
import {
  IonFormPicklistField,
  PicklistOption,
} from '../form/ion-form-picklist.field';
import { IntercomHelp } from '../help/intercom-help';
import { SaveStatusToast } from '../toast/toast.gql';

interface MachineMaintenanceProps {
  id: string;
  teamId: string;
}

const timeFormat = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

const timezoneFormat = new Intl.DateTimeFormat(undefined, {
  dateStyle: undefined,
  timeZoneName: 'short',
});
const timezoneName = timezoneFormat
  .formatToParts(Date.now())
  .find((p) => p.type === 'timeZoneName')?.value;
const timezonePart = timezoneName ? `(${timezoneName})` : ``;

const maintenanceScheduleSchema = yup.object().shape({
  dayOfWeek: yup.string<MachineMaintenanceScheduleDayOfWeek>().required('*'),
  startHour: yup.number().required('*'),
});

const MachineMaintenance = (props: MachineMaintenanceProps): ReactElement => {
  const { id, teamId } = props;
  const { i18n } = useTranslation();

  const { data } = useGetMachineQuery({
    variables: { input: { id, teamId } },
  });

  const machine = data?.getMachine;

  const { update } = usePermissions('machine', machine);
  const isReadOnly = !update;

  const [updateMaintenanceMutation, { loading, error }] =
    useUpdateMachineMaintenanceMutation();

  const maintenance = machine?.state?.maintenance;

  const existingSchedule =
    maintenance?.schedule ?? ({} as MachineMaintenanceSchedule);

  const days = useMemo<PicklistOption[]>(
    () => getDayPicklistOptions(i18n),
    [i18n]
  );

  const hours = useMemo<PicklistOption[]>(() => getHours(), []);

  const onSubmit = useCallback(
    (form: MachineMaintenanceSchedule) => {
      const { __typename, ...schedule } = form;
      const machineQuery = getOperationName(GetMachineDocument);
      updateMaintenanceMutation({
        variables: {
          input: {
            machineId: id,
            schedule,
          },
        },
        refetchQueries: machineQuery ? [machineQuery] : undefined,
      });
    },
    [id, updateMaintenanceMutation]
  );

  return (
    <Formik
      validationSchema={maintenanceScheduleSchema}
      onSubmit={onSubmit}
      initialValues={{ ...existingSchedule, duration: 2 }}
      enableReinitialize={true}
      validateOnMount={true}
    >
      {({ isValid, dirty, submitForm }) => {
        return (
          <>
            <IonList lines="full">
              <IonFormPicklistField
                name="dayOfWeek"
                label={i18n.t('common:maintenance_window_day_of_week')}
                options={days}
                selectProps={{ labelPlacement: 'start' }}
              />
              <IonFormPicklistField
                name="startHour"
                label={i18n.t('common:maintenance_window_start_hour')}
                options={hours}
                selectProps={{ labelPlacement: 'start' }}
              />
            </IonList>
            <SaveStatusToast loading={loading} error={error} />
            <br />
            <RightAlignedButtons>
              <LoadingButton
                loading={loading}
                buttonProps={{
                  disabled: !isValid || !dirty || isReadOnly,
                  onClick: submitForm,
                }}
              >
                {i18n.t('common:maintenance_window_update')}
              </LoadingButton>
            </RightAlignedButtons>
            <div className="content-center">
              <IonText color="medium" className="ion-padding ion-text-center">
                {i18n.t('common:maintenance_window_description')}
              </IonText>
              <IntercomHelp
                articleId={IntercomArticle.MAINTENANCE_WINDOWS}
                description={i18n.t('common:maintenance_window_learn_more')}
              />
            </div>
          </>
        );
      }}
    </Formik>
  );
};

const useGetMachineMaintenanceWindowText = (machine?: Machine): string => {
  const { i18n } = useTranslation();
  const maintenance = machine?.state?.maintenance;

  if (maintenance) {
    const days = getDayPicklistOptions(i18n);
    const hours = getHours();
    const { schedule } = maintenance;
    const dayLabel = days.find((d) => d.value === schedule?.dayOfWeek)?.label;
    const hourLabel = hours.find((h) => h.value === schedule?.startHour)?.label;

    return dayLabel && hourLabel ? `${dayLabel} ${hourLabel}` : ``;
  } else {
    return ``;
  }
};

const getUserLocale = (): string => {
  if (navigator.languages && navigator.languages.length) {
    return navigator.languages[0];
  }
  return navigator.language;
};

const getWeekFirstDay = (): number | undefined => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const locale = new Intl.Locale(getUserLocale()) as any;
  if (typeof locale.getWeekInfo === 'function') {
    // this is the officially supported way with a getter function
    return locale.getWeekInfo().firstDay as number | undefined;
  } else if (typeof locale.weekInfo === 'object') {
    // this is the fallback way that some browsers implemented
    return locale.weekInfo.firstDay as number | undefined;
  }
};

const getDayPicklistOptions = (i18n: i18n): PicklistOption[] => {
  const firstDay = getWeekFirstDay();
  let days = [
    {
      label: i18n.t('common:sunday'),
      value: MachineMaintenanceScheduleDayOfWeek.SUN,
    },
    {
      label: i18n.t('common:monday'),
      value: MachineMaintenanceScheduleDayOfWeek.MON,
    },
    {
      label: i18n.t('common:tuesday'),
      value: MachineMaintenanceScheduleDayOfWeek.TUE,
    },
    {
      label: i18n.t('common:wednesday'),
      value: MachineMaintenanceScheduleDayOfWeek.WED,
    },
    {
      label: i18n.t('common:thursday'),
      value: MachineMaintenanceScheduleDayOfWeek.THU,
    },
    {
      label: i18n.t('common:friday'),
      value: MachineMaintenanceScheduleDayOfWeek.FRI,
    },
    {
      label: i18n.t('common:saturday'),
      value: MachineMaintenanceScheduleDayOfWeek.SAT,
    },
  ];
  if (firstDay !== undefined) {
    // if we hae a `firstDay`, splice of the first part of the days
    // and moved them to the back
    days = days.concat(days.splice(0, firstDay));
  }
  return days;
};

const getHours = (): PicklistOption[] => {
  return Array(24)
    .fill(undefined)
    .map((_, i) => {
      const d = new Date();
      d.setHours(i, 0, 0, 0);
      return {
        label: `${timeFormat.format(d)} ${timezonePart}`,
        value: d.getUTCHours(),
      };
    });
};

export { MachineMaintenance, useGetMachineMaintenanceWindowText };
